/**
 * User Presence Composable
 * Manages workday status, heartbeat, live timer (per-second), and presence state.
 * Pattern based on technician TechDashboard live timer.
 */
import { ref, computed, onUnmounted } from 'vue'
import { presenceApi } from '@/api/tasks'
import { shouldPoll } from '@/composables/usePollingGuard'

const presenceState = ref({
  status: 'offline',
  status_message: '',
  workday_started_at: null,
  current_status_since: null,
  is_online: false,
  today_summary: {
    available_seconds: 0,
    on_hold_seconds: 0,
    busy_seconds: 0,
    disconnected_seconds: 0,
    total_online_seconds: 0,
    login_count: 0,
    status_change_count: 0,
    first_login_at: null,
    last_logout_at: null,
  },
})

const tickCounter = ref(0)
let tickInterval = null
let heartbeatInterval = null
const HEARTBEAT_INTERVAL_MS = 30_000
const TICK_INTERVAL_MS = 1_000

export function usePresence() {
  const isOnline = computed(() => presenceState.value.is_online)
  const status = computed(() => presenceState.value.status)
  const statusMessage = computed(() => presenceState.value.status_message)
  const workdayStarted = computed(() => {
    return presenceState.value.status !== 'offline' && !!presenceState.value.workday_started_at
  })

  const statusColors = {
    available: 'success',
    on_hold: 'warning',
    busy: 'error',
    offline: 'grey',
    disconnected: 'grey-darken-1',
  }
  const statusLabels = {
    available: 'Available',
    on_hold: 'On Hold',
    busy: 'Busy',
    offline: 'Offline',
    disconnected: 'Disconnected',
  }

  const statusColor = computed(() => statusColors[presenceState.value.status] || 'grey')
  const statusLabel = computed(() => statusLabels[presenceState.value.status] || 'Offline')

  /**
   * Parse datetime string from backend as UTC.
   * Backend returns ISO strings without 'Z' suffix -- JS would treat them as local time.
   */
  function parseUTC(dateStr) {
    if (!dateStr) return null
    const s = String(dateStr)
    if (s.endsWith('Z') || s.includes('+')) return new Date(s)
    return new Date(s + 'Z')
  }

  /**
   * Live timer: shows elapsed time in current status, ticking every second.
   * Uses same pattern as TechDashboard liveTimerDisplay.
   */
  const currentStatusTimer = computed(() => {
    void tickCounter.value
    const since = presenceState.value.current_status_since
    if (!since || presenceState.value.status === 'offline') return '0:00'
    const start = parseUTC(since)
    if (!start || isNaN(start.getTime())) return '0:00'
    const now = new Date()
    const elapsed = Math.max(0, Math.floor((now - start) / 1000))
    return formatDurationHMS(elapsed)
  })

  /**
   * Total workday timer (since first login today).
   * base = accumulated time from completed status periods (from backend summary)
   * currentElapsed = live time in current status (computed client-side)
   */
  const workdayTimer = computed(() => {
    void tickCounter.value
    const started = presenceState.value.workday_started_at
    if (!started || presenceState.value.status === 'offline') {
      const total = presenceState.value.today_summary?.total_online_seconds || 0
      return formatDurationHMS(total)
    }
    const base = presenceState.value.today_summary?.total_online_seconds || 0
    const since = presenceState.value.current_status_since
    if (!since) return formatDurationHMS(base)
    const start = parseUTC(since)
    if (!start || isNaN(start.getTime())) return formatDurationHMS(base)
    const currentElapsed = presenceState.value.is_online
      ? Math.max(0, Math.floor((new Date() - start) / 1000))
      : 0
    return formatDurationHMS(base + currentElapsed)
  })

  const todaySummary = computed(() => presenceState.value.today_summary)

  function formatDurationHMS(seconds) {
    if (!seconds || seconds <= 0) return '0:00'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    return `${m}:${String(s).padStart(2, '0')}`
  }

  function formatDurationShort(seconds) {
    if (!seconds || seconds <= 0) return '0s'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  async function fetchMyPresence() {
    try {
      const { data } = await presenceApi.myPresence()
      presenceState.value = {
        ...data,
        is_online: ['available', 'on_hold', 'busy'].includes(data.status),
        today_summary: data.today_summary || presenceState.value.today_summary,
      }
    } catch (e) {
      console.error('[Presence] Failed to fetch:', e)
    }
  }

  async function startWorkday() {
    try {
      await presenceApi.startWorkday()
      await fetchMyPresence()
      startTicking()
      startHeartbeat()
      console.log('[Presence] Workday started')
    } catch (e) {
      console.error('[Presence] Failed to start workday:', e)
      throw e
    }
  }

  async function endWorkday() {
    try {
      await presenceApi.endWorkday()
      await fetchMyPresence()
      stopTicking()
      stopHeartbeat()
      console.log('[Presence] Workday ended')
    } catch (e) {
      console.error('[Presence] Failed to end workday:', e)
      throw e
    }
  }

  async function updateStatus(newStatus, message = '') {
    try {
      await presenceApi.updateStatus({ status: newStatus, status_message: message })
      await fetchMyPresence()
    } catch (e) {
      console.error('[Presence] Failed to update status:', e)
      throw e
    }
  }

  function startTicking() {
    if (tickInterval) return
    tickInterval = setInterval(() => { tickCounter.value++ }, TICK_INTERVAL_MS)
  }

  function stopTicking() {
    if (tickInterval) {
      clearInterval(tickInterval)
      tickInterval = null
    }
  }

  function startHeartbeat() {
    if (heartbeatInterval) return
    heartbeatInterval = setInterval(async () => {
      if (!shouldPoll.value) return
      try {
        await presenceApi.heartbeat()
        await fetchMyPresence()
      } catch (e) {
        console.warn('[Presence] Heartbeat failed:', e)
      }
    }, HEARTBEAT_INTERVAL_MS)
  }

  function stopHeartbeat() {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
  }

  function initPresence() {
    fetchMyPresence().then(() => {
      if (presenceState.value.is_online) {
        startTicking()
        startHeartbeat()
      }
    })
  }

  function destroyPresence() {
    stopTicking()
    stopHeartbeat()
  }

  // Tab close detection: heartbeat timeout on backend handles auto-disconnect.
  // sendBeacon cannot send Authorization headers, so we rely on
  // HEARTBEAT_TIMEOUT_SECONDS (180s) on the server to detect disconnection.

  return {
    presenceState,
    isOnline,
    status,
    statusMessage,
    statusColor,
    statusLabel,
    workdayStarted,
    currentStatusTimer,
    workdayTimer,
    todaySummary,
    fetchMyPresence,
    startWorkday,
    endWorkday,
    updateStatus,
    initPresence,
    destroyPresence,
    statusColors,
    statusLabels,
    formatDurationHMS,
    formatDurationShort,
    tickCounter,
  }
}
